/* eslint-disable no-console */
import type { NextFunction, Request, Response } from 'express'
import { createRateLimiter as createCore } from '../../core/RateLimiter'
import type { RateShieldContext } from '../../context'
import type {
  RateLimitInfo,
  RateLimitOptions,
  RateLimitStats
} from '../../types'
import { extractIp } from './helpers'

// ── Express-specific instance type ─────────────────────────────────────────────

export interface RateLimiterInstance {
  /** Use directly as Express middleware: `app.use(limiter)` */
  (req: Request, res: Response, next: NextFunction): void

  /**
   * Stop the background cleanup timer, remove the process `exit` listener,
   * and destroy all store state. Safe to call multiple times.
   */
  destroy(): void | Promise<void>

  /** Clear all tracking and block data for a single key. */
  reset(key: string): void | Promise<void>

  /** Clear all state — equivalent to restarting the limiter. */
  resetAll(): void | Promise<void>

  /**
   * Current rate-limit snapshot for a key.
   * Returns `null` if the key has never made a request.
   */
  getInfo(key: string): Promise<RateLimitInfo | null>

  /** Aggregate runtime stats for this instance. */
  getStats(): Promise<RateLimitStats>
}

// ── Express factory ────────────────────────────────────────────────────────────

/**
 * Create a rate-limiter instance that works as Express middleware.
 *
 * @example
 * ```ts
 * import { createRateLimiter } from 'rate-shield/express'
 *
 * const limiter = createRateLimiter({ maxRequests: 100, windowMs: 60_000 })
 * app.use(limiter)
 * ```
 */
export const createRateLimiter = (
  options: RateLimitOptions<Request> = {}
): RateLimiterInstance => {
  // Default keyGenerator to extractIp when not provided by the caller.
  const core = createCore<Request>({
    keyGenerator: extractIp,
    ...options
  })

  const middleware = (req: Request, res: Response, next: NextFunction): void => {
    const ctx: RateShieldContext<Request> = {
      req,
      setHeader: (name, value) => {
        if (!res.headersSent) res.setHeader(name, value)
      },
      isResponseSent: () => res.headersSent,
      block: (_statusCode, body) => {
        if (!res.headersSent) res.status(429).json(body)
      },
      pass: () => next()
    }

    core.handle(ctx).catch(err => {
      console.error('[rate-shield] Internal middleware error:', err)
      next()
    })
  }

  return Object.assign(middleware, {
    destroy: () => core.destroy(),
    reset: (key: string) => core.reset(key),
    resetAll: () => core.resetAll(),
    getInfo: (key: string) => core.getInfo(key),
    getStats: () => core.getStats()
  }) as RateLimiterInstance
}
